import { useCallback, useState } from 'react';
import type { GenUiAction } from '../../../shared/ui/action.js';
import type {
  CheckoutFailureReason,
  CheckoutRetry,
  ChatUiProductCheckout,
  CheckoutField,
  CheckoutItem,
} from '@voll-intelligence/genui-contract';
import { buildMultiTravelerCheckoutSubmit } from './checkout_submit.js';
import { travelDocsPending } from './travel_docs.js';
import {
  type CheckoutOfferModel,
  type CheckoutAllocationOwner,
  type CheckoutBudget,
  type CheckoutSubmitBlocker,
  useMultiTravelerCheckoutForm,
  type UseMultiTravelerCheckoutFormResult,
  productContract,
  useBudgetAdvisory,
} from '@voll-intelligence/intelligence-ui-kit/checkout';
import { useProductCheckoutPort, type CheckoutProductType } from '../port.js';
import { checkoutRules } from './checkout_env.js';

/** O desfecho do pedido, dito pelo host — só ele vê o `*_next_services` do
 *  agente chegar (a ligação é do web-chat). `requested` leva o serviço até a
 *  viagem e vira «✓ Solicitado com sucesso!»; `indeterminate` (o fornecedor
 *  não confirmou nem recusou, #5360) vira «em análise», só leitura, sem tentar
 *  de novo — a reserva pode existir; `failed` é a recusa que o turno disse
 *  (`checkout_outcome: failed`, #5514), registro só leitura que aponta para o
 *  card novo (`retry: 'new_checkout'`) ou para a resposta (`'none'`);
 *  `unsent` é do HOST, nunca do agente: o envio falhou na rede ou o turno
 *  estourou o teto sem resposta, e o card volta pronto com o aviso. */
export type CheckoutOutcome =
  | { kind: 'requested' }
  | { kind: 'indeterminate' }
  /** O pedido entrou e espera o gestor (#6699): registro neutro, sem pouso na viagem. */
  | { kind: 'pending_approval' }
  | { kind: 'failed'; retry: CheckoutRetry; reason?: CheckoutFailureReason }
  | { kind: 'unsent' };

/** O ciclo de UM envio, visto do card. `sent` → `inflight` quando o host
 *  liga o `busy` do turno da reserva; o turno acabando com o card ainda vivo
 *  (`readOnly` falso, sem desfecho) é falha — o agente respondeu sem o card de
 *  serviços, que congelaria este. Acabando com o card congelado é `settled`. */
type Submission = 'idle' | 'sent' | 'inflight' | 'failed' | 'settled';

function nextSubmission(
  current: Submission,
  busy: boolean,
  readOnly: boolean,
  outcome: CheckoutOutcome | undefined,
): Submission {
  if (current === 'sent' && busy) return 'inflight';
  if (current !== 'inflight' || busy) return current;
  const answered = outcome !== undefined && outcome.kind !== 'unsent';
  return readOnly || answered ? 'settled' : 'failed';
}

export type OneClickStage =
  /** Sem o 1click: replay, sem host que receba o submit, ou bloco sem roster.
   *  O rodapé fica só com a tela cheia. */
  | { kind: 'off' }
  | { kind: 'loading' }
  /** O form não veio: o card oferece «Tentar de novo» (`form.reload`, #5670) e a
   *  tela cheia segue como saída. */
  | { kind: 'error' }
  /** `notice`: o envio anterior não deu certo. `failed` é a falha deduzida (o
   *  turno acabou sem desfecho — blocos de antes do `failed` explícito, #5514);
   *  `unsent`, a que o host sinalizou (rede, teto sem resposta). */
  | { kind: 'ready'; notice: 'failed' | 'unsent' | null }
  /** `arriving`: o pedido entrou e o serviço está a caminho da viagem (o botão
   *  encolhe e o host leva a bolinha). Só no card que ENVIOU: um card montado
   *  com o desfecho já dito vai direto ao registro. */
  | { kind: 'submitting'; arriving: boolean }
  | { kind: 'done' }
  /** O pedido saiu e o fornecedor não respondeu (#5360): nada vai até a viagem
   *  e nada convida a pedir de novo. */
  | { kind: 'indeterminate' }
  /** O pedido entrou e espera a aprovação do gestor (#6699). */
  | { kind: 'pending_approval' }
  /** A recusa dita pelo turno (#5514): registro só leitura. Com
   *  `new_checkout` o pedido novo está logo abaixo; com `none` a resposta
   *  explica o caminho. */
  | { kind: 'failed'; retry: CheckoutRetry };

export interface UseOneClickCheckoutOptions {
  checkout: ChatUiProductCheckout;
  /** O modelo da oferta do bloco: sem itens não há card, e o hook não roda. */
  offer: CheckoutOfferModel<CheckoutItem>;
  readOnly: boolean;
  busy: boolean;
  outcome?: CheckoutOutcome;
  onSubmit?: (action: Extract<GenUiAction, { type: 'product-checkout-submit' }>) => void;
  /** Os documentos da viagem marcados no card, repassados ao envio. */
  travelDocsChecked?: readonly string[];
}

export interface UseOneClickCheckout {
  stage: OneClickStage;
  form: UseMultiTravelerCheckoutFormResult;
  /** O form pronto E os documentos da viagem todos marcados. */
  canSubmit: boolean;
  /** O que ainda trava o envio, os documentos da viagem inclusive. */
  submitBlockers: CheckoutSubmitBlocker[];
  fields: CheckoutField[];
  withinPolicy: boolean;
  /** Os itens do pedido — o aviso de fora da política nomeia os motivos deles (#5262). */
  items: readonly CheckoutItem[];
  productType: CheckoutProductType;
  /** O total do pedido (por assento × viajantes onde o preço escala). */
  total: number;
  /** O que o rateio de UM bloco cobre: o saldo do centro de custo dele confere
   *  contra isto (o total com um dono só, a parte de cada um quando todos alocam). */
  allocationShare: number;
  /** A data que o saldo confere (início da viagem); ausente, o servidor usa hoje. */
  startDate: string | undefined;
  /** Quem paga: com `'requester'` só o bloco do dono tem rateio no card. */
  allocationOwner: CheckoutAllocationOwner;
  /** Quantos viajantes o roster tem: o esqueleto desenha um bloco por viajante
   *  antes de o form montar as fatias. */
  travelerCount: number;
  /** Envia pelo 1click. No-op fora do estado pronto ou com o form travado. */
  submit: () => void;
  /** Um envio saiu por outro caminho (o drawer de «Revisar dados»). */
  markSent: () => void;
  /** O serviço chegou à viagem: encerra o `arriving`. */
  markArrived: () => void;
}

/** O estágio que o desfecho dito pelo turno decide sozinho; `null` = o card
 *  segue pelo ciclo do envio (sem desfecho, ou o `unsent` da casca). */
function answeredStage(
  outcome: CheckoutOutcome | undefined,
  args: { submission: Submission; arrived: boolean },
): OneClickStage | null {
  switch (outcome?.kind) {
    case 'indeterminate':
      return { kind: 'indeterminate' };
    case 'pending_approval':
      return { kind: 'pending_approval' };
    case 'failed':
      return { kind: 'failed', retry: outcome.retry };
    case 'requested':
      return args.submission === 'idle' || args.arrived
        ? { kind: 'done' }
        : { kind: 'submitting', arriving: true };
    case 'unsent':
    case undefined:
      return null;
    default: {
      const exhaustive: never = outcome;
      return exhaustive;
    }
  }
}

function stageOf(args: {
  submission: Submission;
  arrived: boolean;
  busy: boolean;
  outcome: CheckoutOutcome | undefined;
  armed: boolean;
  phase: UseMultiTravelerCheckoutFormResult['phase'];
}): OneClickStage {
  const { outcome } = args;
  const answered = answeredStage(outcome, args);
  if (answered) return answered;
  if (args.busy || args.submission === 'sent' || args.submission === 'inflight') {
    return { kind: 'submitting', arriving: false };
  }
  if (!args.armed || args.submission === 'settled') return { kind: 'off' };
  if (args.phase !== 'ready') return { kind: args.phase };
  // Aqui o envio só pode estar ocioso ou falho: os demais saíram acima.
  if (outcome?.kind === 'unsent') return { kind: 'ready', notice: 'unsent' };
  return { kind: 'ready', notice: args.submission === 'failed' ? 'failed' : null };
}

/** O saldo de UM centro de custo, consultado a cada troca (o debounce é do
 *  advisory) — no modo padrão do form o advisory dele fica desligado. Só o
 *  insuficiente (`!hasLimit`) interessa ao card: `true` avisa no select, nunca
 *  trava o envio (quem decide é o servidor). Cada bloco de viajante chama o
 *  seu, já que cada um tem o próprio rateio. */
export function useCostCenterShortfall({
  enabled,
  productType,
  allocation,
  total,
  startDate,
}: {
  enabled: boolean;
  productType: CheckoutProductType;
  allocation: { companyId: string; costCenterId: string } | undefined;
  total: number;
  startDate: string | undefined;
}): boolean {
  const port = useProductCheckoutPort();
  const [budget, setBudget] = useState<CheckoutBudget | undefined>(undefined);
  const clear = useCallback(() => {
    setBudget(undefined);
  }, []);
  const onBudgets = useCallback((budgets: readonly (CheckoutBudget | undefined)[]) => {
    setBudget(budgets[0]);
  }, []);
  useBudgetAdvisory({
    io: port,
    enabled,
    productType,
    rows: allocation ? [{ ...allocation, projectId: '' }] : [],
    total,
    startDate,
    onBudget: onBudgets,
    onClear: clear,
  });
  return budgetShortfall(budget);
}

/** O saldo que não cobre: só ele interessa ao card. */
export function budgetShortfall(budget: CheckoutBudget | undefined): boolean {
  return Boolean(budget?.hasControl && !budget.hasError && !budget.hasLimit);
}

/** A corrida que já traz o contato do passageiro (#6699, «Detalhes da corrida»)
 *  não pede o telefone de novo no card: o número vale só para ela e o agente o
 *  guarda — nunca no perfil de quem viaja. */
function contactCovered(items: readonly CheckoutItem[]): boolean {
  return items.some((item) => item.productType === 'mobility' && Boolean(item.detail.contact));
}

/** O estado do card 1click: o MESMO form do drawer (`useMultiTravelerCheckoutForm`,
 *  com o MESMO manifesto e o MESMO builder do envio) e o ciclo do pedido. Só
 *  busca os forms quando o 1click está armado — um card em replay não dispara
 *  `getBookingForm` pela simples renderização. */
export function useOneClickCheckout({
  checkout,
  offer,
  readOnly,
  busy,
  outcome,
  onSubmit,
  travelDocsChecked,
}: UseOneClickCheckoutOptions): UseOneClickCheckout {
  const travelers = checkout.travelers ?? [];
  const { productType, withinPolicy, allocationOwner, allocationShare, startDate } = offer;
  const fields = contactCovered(checkout.items)
    ? offer.fields.filter((field) => field.kind !== 'phone')
    : offer.fields;
  const first = offer.item;
  const armed = !readOnly && Boolean(onSubmit) && travelers.length > 0;
  const total = offer.total.amount;
  // O MESMO form da tela cheia, com o MESMO dono do rateio, data e total.
  const port = useProductCheckoutPort();
  const form = useMultiTravelerCheckoutForm({
    io: port,
    rules: checkoutRules,
    productType,
    fields,
    open: armed,
    travelers,
    requesterId: checkout.requesterId,
    policyTravelerId: checkout.policyTravelerId,
    withinPolicy,
    allocationOwner,
    guestsPerRoom: productContract(first).guestsPerRoom(first),
    prefill: checkout.prefill,
    total,
    startDate,
  });

  const [stored, setStored] = useState<Submission>('idle');
  const submission = nextSubmission(stored, busy, readOnly, outcome);
  // Ajuste no render (não em efeito): a transição depende só das props deste
  // render, e um efeito pintaria um quadro com o estado velho.
  if (submission !== stored) setStored(submission);
  const [arrived, setArrived] = useState(false);

  const stage = stageOf({ submission, arrived, busy, outcome, armed, phase: form.phase });
  const docsPending = travelDocsPending(checkout.travelDocs, travelDocsChecked ?? []);
  const canSubmit = form.canSubmit && !docsPending;
  const submitBlockers: CheckoutSubmitBlocker[] = docsPending
    ? [...form.submitBlockers, 'travel_docs']
    : form.submitBlockers;

  const submit = () => {
    if (stage.kind !== 'ready' || !canSubmit || !onSubmit) return;
    onSubmit(
      buildMultiTravelerCheckoutSubmit({
        challengeId: checkout.challengeId,
        item: first,
        perTraveler: form.travelers.map((slice) => {
          if (!slice.form) throw new Error('formulário do viajante ausente — reabra o checkout');
          return {
            traveler: slice.traveler,
            form: slice.form,
            allocations: slice.allocations,
            travelerDraft: slice.travelerDraft,
          };
        }),
        motiveId: form.motiveId,
        justificationId: form.justificationId,
        observation: form.observation,
        policyTravelerId: form.policyTravelerId,
        allocationOwner,
        requesterId: checkout.requesterId,
        travelDocsChecked,
        travelDocs: checkout.travelDocs,
      }),
    );
    setStored('sent');
  };

  return {
    stage,
    form,
    canSubmit,
    submitBlockers,
    fields,
    withinPolicy,
    items: checkout.items,
    productType,
    total,
    allocationShare,
    startDate,
    allocationOwner,
    travelerCount: travelers.length,
    submit,
    markSent: () => {
      setStored('sent');
    },
    markArrived: () => {
      setArrived(true);
    },
  };
}
